// vg-binding: P7.D-18
// vg-binding: P7.D-21
//
// apps/vendor/src/features/products/add/CatalogPicker.tsx
//
// Phase 7 Wave 8 Task 28 — catalog blueprint picker.
//
// dbg-vendor-catalog-picker — replaced the Wave 8 free-form text input
// (which forced the vendor to type a raw blueprint ObjectId by hand —
// there was no surface to discover one) with a searchable list backed by
// GET /api/v1/vendor/catalog/blueprints. Selecting a row sets `source_id`
// to the blueprint's `id`. The endpoint strips tier pricing
// and returns the localized `name` as a `{ en, vi }` object.

import { useState } from 'react';

import { useTranslation } from '../../../lib/i18n/index.js';
import {
  useVendorCatalog,
  type CatalogBlueprint,
  type LocalizedName,
} from '../api.js';

interface Props {
  source_id: string;
  onChange: (next: string) => void;
}

/** Resolve a `{ en, vi, fr }` name (or plain string) to the active UI
 *  language, falling back to en → vi → fr → any non-empty value. */
function localizeName(name: LocalizedName, lang: string): string {
  if (typeof name === 'string') return name;
  if (!name) return '';
  const short = (lang || 'en').slice(0, 2).toLowerCase() as 'en' | 'vi' | 'fr';
  return (
    name[short] ??
    name.en ??
    name.vi ??
    name.fr ??
    Object.values(name).find((v) => typeof v === 'string' && v.length > 0) ??
    ''
  );
}

export function CatalogPicker({ source_id, onChange }: Props): JSX.Element {
  const { t, i18n } = useTranslation('products');
  const [search, setSearch] = useState('');
  // pageSize is capped at 20 server-side (catalog.schemas CatalogListQuery);
  // search narrows the list, so a single page is enough for the picker.
  const { data, isLoading, isError } = useVendorCatalog({
    search,
    pageSize: 20,
  });

  const lang = i18n.language;
  const items: CatalogBlueprint[] = data?.items ?? [];
  const selected = items.find((b) => b.id === source_id) ?? null;

  return (
    <section
      data-testid="catalog-picker"
      className="rounded-xl border border-pw-border bg-pw-bg p-4"
    >
      <h3 className="text-sm font-semibold text-pw-text">{t('catalog.title')}</h3>
      <p className="mt-1 text-xs text-pw-text-muted">{t('catalog.description')}</p>

      <div className="mt-3">
        <input
          data-testid="catalog-search-input"
          type="search"
          className="h-10 w-full rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('catalog.searchPlaceholder')}
        />
      </div>

      {selected ? (
        <div
          data-testid="catalog-selected"
          className="mt-3 flex items-center gap-2 rounded-md border border-pw-primary/40 bg-pw-primary/5 px-3 py-2 text-sm text-pw-text"
        >
          <span className="font-medium">{t('catalog.selectedLabel')}</span>
          <span>{localizeName(selected.name, lang)}</span>
          {selected.blueprintCode ? (
            <span className="text-xs text-pw-text-muted">
              ({selected.blueprintCode})
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3 max-h-80 overflow-y-auto rounded-md border border-pw-border">
        {isLoading ? (
          <p className="px-3 py-4 text-sm text-pw-text-muted">
            {t('catalog.loading')}
          </p>
        ) : isError ? (
          <p
            data-testid="catalog-error"
            className="px-3 py-4 text-sm text-red-700"
          >
            {t('catalog.loadError')}
          </p>
        ) : items.length === 0 ? (
          <p
            data-testid="catalog-empty"
            className="px-3 py-4 text-sm text-pw-text-muted"
          >
            {t('catalog.empty')}
          </p>
        ) : (
          <ul data-testid="catalog-list" className="divide-y divide-pw-border">
            {items.map((bp) => {
              const isSelected = bp.id === source_id;
              const thumb = bp.coverImageUrl ?? null;
              const variantCount = bp.variantCount ?? bp.variants?.length ?? 0;
              const label = localizeName(bp.name, lang);
              return (
                <li key={bp.id}>
                  <button
                    type="button"
                    data-testid={`catalog-item-${bp.id}`}
                    onClick={() => onChange(bp.id)}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-pw-primary/5 ${
                      isSelected ? 'bg-pw-primary/10' : ''
                    }`}
                  >
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="h-10 w-10 flex-shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 flex-shrink-0 rounded bg-pw-border" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-pw-text">
                        {label}
                      </p>
                      <p className="truncate text-xs text-pw-text-muted">
                        {bp.blueprintCode ? `${bp.blueprintCode} · ` : ''}
                        {t('catalog.variantCount', { count: variantCount })}
                      </p>
                    </div>
                    {isSelected ? (
                      <span className="flex-shrink-0 text-xs font-semibold text-pw-primary">
                        ✓
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

export default CatalogPicker;
